import { Layers, ShieldCheck, Smartphone, type LucideIcon } from "lucide-react";

/** One supporting point under the mission statement. */
interface AboutPoint {
  icon: LucideIcon;
  title: string;
  description: string;
}

/**
 * Three supporting points that ground the mission without fabricated stats.
 * Each maps to a real product stance from the platform docs (software-not-a-
 * carrier, document portability, mobile-first).
 */
const POINTS: readonly AboutPoint[] = [
  {
    icon: Layers,
    title: "Software, not a carrier",
    description:
      "We provide the console — booking, dispatch, coordination, messaging. You provide the service. Your customers stay yours.",
  },
  {
    icon: ShieldCheck,
    title: "Portable by design",
    description:
      "Drivers carry their credentials with them and work with multiple operators without re-uploading the same documents to each one.",
  },
  {
    icon: Smartphone,
    title: "Mobile-first, always",
    description:
      "Operators and drivers run the day from a phone, so every surface is built to be fast and usable on a small screen first.",
  },
];

/**
 * Concise homepage About band — a tight "what RideReserv is" statement that
 * replaces the deleted /about page. A short mission paragraph plus three
 * supporting points; deliberately not a wall of text and with no fabricated
 * stats or customer names. The page shell wraps this with `id="about"` so the
 * About nav link scrolls here.
 *
 * Presentational and self-contained; token classes only; flip-safe for dark.
 * @returns The about section
 */
export function AboutSection() {
  return (
    <section
      aria-labelledby="about-heading"
      className="border-b border-neutral-200 bg-neutral-100"
    >
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,28rem)_minmax(0,1fr)] lg:gap-16">
          {/* Mission statement */}
          <div>
            <span className="inline-flex items-center gap-1.5 font-mono text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
              <span aria-hidden="true" className="h-px w-6 bg-primary-600" />
              About RideReserv
            </span>
            <h2
              id="about-heading"
              className="mt-4 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl"
            >
              Built for the people who keep cars moving
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-neutral-600">
              RideReserv is reservation and dispatch software for independent
              transportation operators — the drivers and limo services that run
              the rides, without building their own software. We build the
              console in between spreadsheets and enterprise tools: powerful
              enough to run a real operation, simple enough to set up in an
              afternoon.
            </p>
          </div>

          {/* Supporting points */}
          <ul className="grid grid-cols-1 gap-px self-center overflow-hidden rounded-md border border-neutral-200 bg-neutral-200 sm:grid-cols-3">
            {POINTS.map(({ icon: Icon, title, description }) => (
              <li key={title} className="flex flex-col bg-surface p-5">
                <span
                  aria-hidden="true"
                  className="flex h-9 w-9 items-center justify-center rounded-sm border border-neutral-200 text-primary-600 dark:text-primary-400"
                >
                  <Icon className="h-4 w-4" strokeWidth={1.75} />
                </span>
                <h3 className="mt-4 text-base font-semibold text-neutral-900">
                  {title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">
                  {description}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
